// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import _ from 'lodash';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import classNames from 'classnames';
import isUaWebview from 'is-ua-webview';
import { StarRatingDisplay } from '@nexus/fff-ratings-and-reviews';
import Button from '@nexus/fff-ui-shared/Button';
import FeatureList from '@nexus/fff-ui-shared/FeatureList';

import { updateLocationSearch } from 'utils/url';
import { trackInviteStarted } from 'utils/social-ecomm';
import {
  segmentEnum,
  btnLocationEnum,
  track,
  trackOptimizelyExperiment,
} from 'utils/segment';
import { VIEW_SIZE } from 'utils/device';
import { getCampaignType, VERSION } from 'utils/campaign';
import { formatCurrency } from 'utils/currency';
import { PRODUCT_PURCHASE_OPTIONS, CADENCE_TYPE } from 'utils/subscriptions';
import {
  PARIS_HILTON_SKU,
  PH_FEATURE_LIST_BEIGE,
} from 'utils/parisHiltonConstants';

import { selectTrackingData } from 'selectors/trackingSelector';
import {
  selectSubtotal,
  selectHasSponsoredProductInCart,
} from 'selectors/cartSelectors';
import { selectIsSocialEcomm } from 'selectors/socialEcommSelectors';
import { selectProductRecommendations } from 'selectors/productsSelectors';

import {
  requestAddToCart,
  requestRemoveFromCart,
  requestClearVariantItemsFromCart,
} from 'services/actions/cartActions';
import {
  hideSponsoredWarning,
  setOverrideDefaultVariantId,
  requestProductRecommendations as requestProductRecommendationsAction,
  requestRatingGraph,
  requestProductHasReviewed,
  receiveProductHasReviewed,
} from 'services/actions/productsActions';
import { openShareInviteModal as openShareInviteModalAction } from 'services/actions/socialEcommActions';

import withSelectedVariant from 'HOC/withSelectedVariant/withSelectedVariant';
import withSelectedCategoryId from 'HOC/withSelectedCategoryId/withSelectedCategoryId';
import withVersion from 'HOC/withVersion/withVersion';

import CloseButton from 'shared-components/CloseButton/CloseButton';
import ProductReviewCTA from 'Scenes/ProductDetail/ProductReviewCTA';

import type { VariantDescriptor, State } from 'sharedTypes';
import UpdateCartButton from '../UpdateCartButton/UpdateCartButton';
import Gallery from '../Gallery/Gallery';
import FavButton from '../FavButton/FavButton';
import Variants from '../Variants/Variants';
import TeamAvatars from '../TeamAvatars/TeamAvatars';
import SimilarProducts from '../SimilarProducts/SimilarProducts';

import styles from './ProductAccordion.module.css';
import ratingStyles from './ProductRatings.module.css';

import CollapsibleSection from './CollapsibleSection/CollapsibleSection';
import ProductDescription from '../../ProductDetail/ProductDescription/ProductDescription';
import ProductRatings from './ProductRatings';
import ProductRatingsTitle from './ProductRatingsTitle';
import SubscriptionBadge from '../SubscriptionBadge/SubscriptionBadge';
import SubscriptionSelector from '../SubscriptionSelector/SubscriptionSelector';
import SubscriptionModalConfirmation from '../SubscriptionModalConfirmation/SubscriptionModalConfirmation';
import ProductPricing from '../ProductPricing/ProductPricing';
import ActiveSubscriptionBanner from '../ActiveSubscriptionBanner/ActiveSubscriptionBanner';
import NotifyMeButton from '../NotifyMeButton/NotifyMeButton';
import SubscribeAndSaveDetails from '../SubscribeAndSaveDetails/SubscribeAndSaveDetails';

type InjectedProps = {|
  +selectedVariant: BundledVariant | null,
  +selectedDescriptors: Array<VariantDescriptor>,
  +updateSelectedVariant: (id: string) => void,
  +updateSelectedDescriptors: (descriptor: VariantDescriptor) => void,
  +selectedCategoryId: string | null,
  +version: string,
  +flags: {|
    shortDescriptionFields: boolean,
    socialEcomm: boolean,
    rateAndReview: boolean,
  |},
  +isSocialEcomm: boolean,
  +similarProducts: Array<BundledProduct>,
  +ratings: {| key: number, value: Ratings |},
  +productRatingGraph: {|
    +averageRating: number,
    +totalReviewCount: number,
    +ratingDistribution: Array<{ [number]: number }>,
  |},
  +productHasReviewed: {|
    +productId: number,
    +hasReviewed: boolean,
  |},
|};

type Ratings = {|
  +averageRating: number,
  +totalReviewCount: number,
|};

type OwnProps = {|
  ...BundledProduct,
  +gwpProducts: Array<BundledProduct>,
  +isLearnMoreVisible: boolean,
  +positionLocation?: string,
|};

type DispatchProps = {|
  +requestRatingGraph: typeof requestRatingGraph,
  +requestProductHasReviewed: typeof requestProductHasReviewed,
  +requestAddToCart: typeof requestAddToCart,
  +requestRemoveFromCart: typeof requestRemoveFromCart,
  +hideSponsoredWarning: typeof hideSponsoredWarning,
  +setOverrideDefaultVariantId: typeof setOverrideDefaultVariantId,
  +requestClearVariantItemsFromCart: typeof requestClearVariantItemsFromCart,
|};

type StatePropsForAccordion = {|
  +sponsoredThreshold: number,
  +subtotal: number,
  +variantToRemove: string | null,
  +viewSize: string,
  +trackingData: TrackingData,
  +hasSponsoredProductInCart: boolean,
  +teamId: number | null,
  +isSocialEcomm: boolean,
  +fetchingSimilarProducts: boolean,
  +loggedIn: boolean,
|};

// We pass overrideDefaultVariantId to WithSelectedVariant,
// but WithSelectedVariant doesn't pass overrideDefaultVariantId through
type StatePropsForWithSelectedVariant = {|
  ...StatePropsForAccordion,
  +overrideDefaultVariantId: string | null,
|};

type Props = {|
  ...InjectedProps,
  ...OwnProps,
  ...DispatchProps,
  ...StatePropsForAccordion,
|};

type ComponentState = {|
  +touchStart: number,
  +isDetailsOpen: boolean,
  +isRatingsOpen: boolean,
  +productPurchaseOption: string,
  +showSubscriptionModal: boolean,
|};

const formatPrice = (price) => formatCurrency(Number(price));

class ProductAccordion extends React.PureComponent<Props, ComponentState> {
  state = {
    touchStart: 0,
    isDetailsOpen: true,
    isRatingsOpen: true,
    productPurchaseOption: '',
    showSubscriptionModal: false,
  };

  constructor(props) {
    super(props);
    this.accordionContainer = React.createRef();
    this.reviewsHeaderRef = React.createRef();
  }

  componentDidMount() {
    const {
      id,
      loggedIn,
      requestRatingGraph,
      requestProductHasReviewed,
      version,
    } = this.props;

    this.handleScrolling();
    this.handleProductViewedTracking();
    this.resetOverrideDefaultVariantId();
    requestRatingGraph(id);
    if (loggedIn) requestProductHasReviewed(id);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      id: prevId,
      isLearnMoreVisible: prevIsLearnMoreVisible,
      optimizelySimilarProducts: prevOptimizelySimilarProducts,
      fetchingSimilarProducts: prevFetchingSimilarProducts,
      selectedVariant: prevSelectedVariant,
    } = prevProps;
    const {
      id,
      isLearnMoreVisible,
      optimizelySimilarProducts,
      fetchingSimilarProducts,
      selectedVariant,
      version,
    } = this.props;
    const displayPurchaseOptions =
      version === VERSION.shop || version === VERSION.boosts;
    if (prevId !== id) {
      this.scrollIntoView();
      this.getSimilarProducts();
    }
    if (prevIsLearnMoreVisible !== isLearnMoreVisible) {
      this.handleBodyScrollLocks();
    }
    if (prevOptimizelySimilarProducts !== optimizelySimilarProducts) {
      this.getSimilarProducts();
      this.handleOptimizelyEvents();
    }
    if (
      prevFetchingSimilarProducts !== fetchingSimilarProducts &&
      !fetchingSimilarProducts
    ) {
      this.handleOpenAccordionWithSimilarProductsTracking();
    }
    if (displayPurchaseOptions && prevSelectedVariant !== selectedVariant) {
      this.handleProductPurchaseOptionInitialState();
    }
  }

  componentWillUnmount() {
    clearAllBodyScrollLocks();
  }

  getSimilarProducts = async () => {
    const { id, requestProductRecommendations, optimizelySimilarProducts } =
      this.props;
    const { enabled: similarProductsFF, variationName } =
      optimizelySimilarProducts;
    if (
      similarProductsFF &&
      variationName === SIMILAR_PRODUCTS_DECISIONS.similarRecs
    ) {
      const campaignType = getCampaignType();
      const limit = 24;
      requestProductRecommendations(id, campaignType, limit);
    }
  };

  resetOverrideDefaultVariantId = () =>
    this.props.setOverrideDefaultVariantId(null);

  handleScrolling = () => {
    const { viewSize } = this.props;
    const currentAccordionContainer = this.accordionContainer.current;
    if (viewSize === VIEW_SIZE.small) {
      if (currentAccordionContainer && !document.body.style.overflow)
        disableBodyScroll(currentAccordionContainer);
    } else {
      this.scrollIntoView();
    }
  };

  handleBodyScrollLocks = () => {
    const { viewSize, isLearnMoreVisible } = this.props;
    const currentAccordionContainer = this.accordionContainer.current;
    if (viewSize === VIEW_SIZE.small && currentAccordionContainer) {
      if (isLearnMoreVisible) clearAllBodyScrollLocks();
      else disableBodyScroll(currentAccordionContainer);
    }
  };

  handleProductPurchaseOptionInitialState = () => {
    const { flags, selectedVariant } = this.props;
    const {
      quantity,
      isSubscriptionAvailable,
      cadenceTypeInCart,
      hasActiveSubscription,
    } = selectedVariant;
    const { productPurchaseOption } = this.state;
    const { alternateSubscriptions: alternateSubscriptionsFF } = flags;
    const subscriptionAvailable =
      alternateSubscriptionsFF && isSubscriptionAvailable;
    const productPurchaseOptionDefault =
      // use previous state if it exists
      productPurchaseOption ||
      // display save & subscribe if there is NO active subscription
      // AND there is a subscription in the cart OR cart quantity is 0 is it is a subscribable product
      (!hasActiveSubscription &&
      ((quantity > 0 && cadenceTypeInCart) ||
        (quantity === 0 && subscriptionAvailable))
        ? PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE
        : PRODUCT_PURCHASE_OPTIONS.ONE_TIME);
    this.setState({
      productPurchaseOption: productPurchaseOptionDefault,
    });
  };

  handleProductViewedTracking = () => {
    const { id, trackingData, positionLocation, ratings } = this.props;
    const { productViewed } = segmentEnum;
    const productRating = _.get(ratings, id, null);

    track(productViewed, {
      ...trackingData,
      experience: btnLocationEnum.productAccordion,
      ...(positionLocation && { position_location: positionLocation }),
      component_type: null,
      component_label: null,
      total_recs_displayed: null,
      ...(productRating && {
        star_rating: productRating.averageRating,
        total_reviews: productRating.totalReviewCount,
      }),
    });
  };

  handleOptimizelyEvents = () => {
    const { trackingData, optimizelySimilarProducts } = this.props;
    const { enabled: similarProductsFF, variationName } =
      optimizelySimilarProducts;

    // fire the "experiment viewed" + "identify" events
    if (similarProductsFF) {
      const {
        user_id: userId,
        campaign_id: campaignId,
        plan_code: userPlanCode,
      } = trackingData;
      trackOptimizelyExperiment(
        optimizelySimilarProducts,
        userId,
        campaignId,
        userPlanCode
      );
    }
  };

  handleOpenAccordionWithSimilarProductsTracking = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrerProductId = urlParams.get('referrer_product_id');
    const { id, trackingData, positionLocation, similarProducts, ratings } =
      this.props;
    const { productViewed } = segmentEnum;
    const productRating = _.get(ratings, id, null);
    track(productViewed, {
      ...trackingData,
      experience: referrerProductId
        ? btnLocationEnum.recommendedAccordion
        : btnLocationEnum.productAccordion,
      ...(positionLocation && { position_location: positionLocation }),
      component_type: 'p2p recommendation',
      component_label: 'Similar Products',
      total_recs_displayed: similarProducts.length,
      ...(referrerProductId && {
        referrer_parent_product_id: referrerProductId,
      }),
      ...(productRating && {
        star_rating: productRating.averageRating,
        total_reviews: productRating.totalReviewCount,
      }),
    });
  };

  handleInviteStartedTracking = () => {
    const { trackingData } = this.props;
    trackInviteStarted(
      _.omit(trackingData, [
        'parent_product_id',
        'position_location',
        'product_image_url',
        'product_categories',
      ]),
      btnLocationEnum.productAccordion,
      'SHARE & SEND INVITE'
    );
  };

  handleClickConfirmRemoval = () => {
    const { selectedVariant, requestRemoveFromCart, id, selectedCategoryId } =
      this.props;
    if (selectedVariant !== null) {
      requestRemoveFromCart(
        selectedVariant.id,
        id,
        btnLocationEnum.productAccordion,
        '-',
        selectedCategoryId
      );
    }
  };

  toggleProductCollapsible = (stateKey: 'isRatingsOpen' | 'isDetailsOpen') => {
    this.setState((state) => {
      return { [stateKey]: !state[stateKey] };
    });
  };

  // callback functions for the cms
  sysdata = {
    callBacks: {
      Button_F1a888310_3744_4e6f_84b8_865b3668aa84:
        this.handleClickConfirmRemoval,
      Button_b339ece4a_7ef7_41fd_9a39_cb7dfe6ffbec:
        this.props.hideSponsoredWarning,
    },
  };

  scrollIntoView = () => {
    const header = document.querySelector('.header-wrapper');
    const height = header ? -header.offsetHeight : -170;
    const currentAccordionContainer = this.accordionContainer.current;
    if (currentAccordionContainer !== null) {
      currentAccordionContainer.scrollIntoView();
      window.scrollBy(0, height);
    }
  };

  disableBodyScrollAccordion = () => {
    const currentAccordionContainer = this.accordionContainer.current;
    if (currentAccordionContainer !== null) {
      disableBodyScroll(currentAccordionContainer);
    }
  };

  handleSwipeStart = (e: SyntheticTouchEvent<>) =>
    this.setState({ touchStart: e.touches[0].clientX });

  handleSwipeEnd = (e: SyntheticTouchEvent<>) => {
    const { touchStart } = this.state;
    const { viewSize } = this.props;
    const isMobile = viewSize === VIEW_SIZE.small;

    const touchEnd = e.changedTouches[0].clientX;
    if (isMobile && touchEnd - touchStart > 200) {
      window.history.back();
    }
  };

  handleClickShare = (percentOff, imageUrl) => {
    const { id, brandName, name, openShareInviteModal, selectedVariant } =
      this.props;
    const { salePrice, price, sku } = selectedVariant;
    const product = {
      productId: id,
      discountPercent: percentOff,
      productName: name,
      productBrand: brandName,
      thumbnailImage: imageUrl,
      salePrice,
      retailPrice: price,
      sku,
    };
    openShareInviteModal(product, btnLocationEnum.productAccordion);
    this.handleInviteStartedTracking();
  };

  render() {
    const {
      id,
      legacyPostId,
      name,
      viewSize,
      selectedVariant,
      selectedDescriptors,
      variants,
      updateSelectedVariant,
      updateSelectedDescriptors,
      shareUrl,
      isFavorite,
      sponsored,
      sponsoredThreshold,
      subtotal,
      hasSponsoredProductInCart,
      gwpThreshold,
      gwp,
      gwpProducts,
      brandName,
      shortDescription,
      includes,
      features,
      color,
      design,
      size,
      dimensions,
      howToUse,
      ingredients,
      materials,
      techCompatibility,
      careInstructions,
      pao,
      additionalInformation,
      countryOfOrigin,
      dropshipDisclaimer,
      paoDisclaimer,
      cbdDisclaimer,
      description,
      flags,
      isSocialEcomm,
      teamCart,
      className,
      positionLocation,
      similarProducts,
      ratings,
      productRatingGraph,
      reviewedProducts,
      loggedIn,
      slug,
      subscribableProducts,
      isSubscribableShopProduct,
      version,
    } = this.props;
    if (!selectedVariant) return null;

    const isWebview = isUaWebview(navigator.userAgent);
    const isMobile = viewSize === VIEW_SIZE.small;
    const productRating = _.get(ratings, id, null);
    const hasReviews = productRating?.totalReviewCount > 0;
    const {
      isDetailsOpen,
      isRatingsOpen,
      productPurchaseOption,
      showSubscriptionModal,
    } = this.state;
    const isShopPage = version === VERSION.shop;
    const isSubscribeSaveSelected =
      isShopPage &&
      productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE;

    const {
      shortDescriptionFields: shortDescriptionFieldsFF,
      socialEcomm: socialEcommFF,
      rateAndReview: rateAndReviewFF,
      alternateSubscriptions: alternateSubscriptionsFF,
    } = flags;
    const threashold = gwp ? gwpThreshold : sponsoredThreshold;
    const { percentOff, galleryUrls, imageUrl } = selectedVariant;
    const subscriptionPercentOff =
      selectedVariant.subscription?.percentOff || 0;
    const galleryUrlsWithPrimaryImage = [imageUrl, ...galleryUrls];
    const isLowInventory =
      selectedVariant !== null &&
      !selectedVariant.soldOut &&
      selectedVariant.lowInventory;
    const displayTeamAvatars = socialEcommFF && teamCart;
    const urlParams = new URLSearchParams(window.location.search);
    const referrerProductId = urlParams.get('referrer_product_id');
    const positionOrder = urlParams.get('position_order');
    const hasBeenReviewed = reviewedProducts[id];
    const ratingsReviewHeader = this.reviewsHeaderRef.current;
    const isSubscriptionAvailable =
      alternateSubscriptionsFF && selectedVariant?.isSubscriptionAvailable;
    const activeSubscriptionQty =
      selectedVariant?.subscription?.activeSubscription?.quantity || 0;

    function priceDisplay(priceType: 'salePrice' | 'price') {
      let maxPrice = 0;
      let minPrice = Infinity;
      for (const variant of variants) {
        const priceName =
          priceType === 'salePrice' && variant.salePrice === null
            ? 'price'
            : priceType;
        if (parseFloat(variant[priceName]) > parseFloat(maxPrice)) {
          maxPrice = variant[priceName];
        }
        if (parseFloat(variant[priceName]) < parseFloat(minPrice)) {
          minPrice = variant[priceName];
        }
      }
      if (selectedVariant !== null) {
        const priceName =
          priceType === 'salePrice' && selectedVariant.salePrice === null
            ? 'price'
            : priceType;
        return formatPrice(selectedVariant[priceName]);
      }
      if (minPrice !== maxPrice) {
        return `${formatPrice(minPrice)}-${formatPrice(maxPrice)}`;
      }
      return formatPrice(maxPrice);
    }

    const toggleShowSubscriptionModal = () => {
      this.setState((prevState) => ({
        showSubscriptionModal: !prevState.showSubscriptionModal,
      }));
    };

    const handleSubscribeChange = (event) => {
      const { value } = event.target;
      this.setState({ productPurchaseOption: value });
      if (selectedVariant.quantity > 0) {
        toggleShowSubscriptionModal();
      }
    };

    const cancelSubscriptionModal = () => {
      this.setState({
        productPurchaseOption:
          productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.ONE_TIME
            ? PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE
            : PRODUCT_PURCHASE_OPTIONS.ONE_TIME,
      });
      toggleShowSubscriptionModal();
    };

    const confirmSubscriptionModal = () => {
      const { selectedCategoryId, requestClearVariantItemsFromCart } =
        this.props;
      const { id, parentId, quantity } = selectedVariant;
      const cadenceType =
        (quantity > 0 && !productPurchaseOption && isSubscriptionAvailable) ||
        productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE
          ? CADENCE_TYPE.NINETY_DAYS
          : null;
      const buttonLocation = btnLocationEnum.productAccordion;
      const buttonName = 'ADD TO CART';

      requestClearVariantItemsFromCart(
        id,
        parentId,
        buttonLocation,
        buttonName,
        selectedCategoryId,
        positionLocation,
        referrerProductId,
        positionOrder,
        cadenceType
      );
      toggleShowSubscriptionModal();
    };

    const price = priceDisplay('price');
    const salePrice = priceDisplay('salePrice');
    const subscriptionPrice = formatPrice(
      selectedVariant.subscription.salePrice || 0
    );
    const subscriptionRetailPrice = formatPrice(
      selectedVariant.subscription.retailPrice || 0
    );
    const displayedPrice = isSubscribeSaveSelected
      ? subscriptionRetailPrice
      : price;
    const displayedSalePrice = isSubscribeSaveSelected
      ? subscriptionPrice
      : salePrice;
    const displayedPercentOff = isSubscribeSaveSelected
      ? subscriptionPercentOff || 0
      : percentOff;
    const isParisHiltonKit = selectedVariant.sku === PARIS_HILTON_SKU;

    return (
      <section
        className={classNames(
          styles.accordionContainer,
          styles[viewSize],
          className,
          {
            [styles.sponsored]: sponsored,
          }
        )}
        ref={this.accordionContainer}
        data-testid="product-accordion-section"
      >
        {displayTeamAvatars && (
          <TeamAvatars
            list={teamCart}
            className={styles.teamCart}
            context="accordion"
          />
        )}
        <Link
          to={updateLocationSearch({ product_link: null })}
          className={styles.closeLink}
          data-dd-action-name="Product accordion close button"
        >
          <CloseButton />
        </Link>
        {isSubscriptionAvailable && (
          <div className={styles.subscriptionBadge}>
            <SubscriptionBadge />
          </div>
        )}
        <div className={styles.galleryContainer}>
          <Gallery
            images={galleryUrlsWithPrimaryImage}
            viewSize={viewSize}
            disableBodyScroll={disableBodyScroll}
            disableBodyScrollAccordion={this.disableBodyScrollAccordion}
            context="accordion"
            isLowInventory={isLowInventory}
          />
        </div>
        <div
          className={styles.detailContainer}
          onTouchStart={this.handleSwipeStart}
          onTouchEnd={this.handleSwipeEnd}
        >
          <div
            className={classNames(styles.productSummary, {
              [styles.productSummaryGallery]: !!galleryUrls.length,
            })}
          >
            {!!activeSubscriptionQty && (
              <ActiveSubscriptionBanner
                subscriptionsQty={activeSubscriptionQty}
              />
            )}
            <div className={styles.titleWrapper}>
              <h3 className={styles.brandName}>{brandName}</h3>
              <div className={styles.nameContainer}>
                <h1 className={styles.name}>{name}</h1>
                {loggedIn && !sponsored && (
                  <FavButton
                    isFavorite={isFavorite}
                    productId={id}
                    variantId={
                      selectedVariant === null
                        ? _.get(variants, [0, 'id'])
                        : _.get(selectedVariant, 'id')
                    }
                    buttonLocation={
                      referrerProductId
                        ? btnLocationEnum.recommendedAccordion
                        : btnLocationEnum.productAccordion
                    }
                    positionLocation={positionLocation}
                    referrerProductId={referrerProductId}
                    positionOrder={positionOrder}
                  />
                )}
              </div>
            </div>
            {productRating && (
              <div className={styles.ratingAndCtaContainer}>
                <div className={styles.ratingContainer}>
                  <StarRatingDisplay
                    rating={productRating.averageRating}
                    ratingCount={productRating.totalReviewCount}
                    size="large"
                    theme="pink"
                  />
                </div>
                <ProductReviewCTA
                  loggedIn={loggedIn}
                  productHasReviewed={hasBeenReviewed}
                  productHasReviews={hasReviews}
                  isProductRatingSectionOpen={isRatingsOpen}
                  onClickCTALink={() => {
                    this.toggleProductCollapsible('isRatingsOpen');
                  }}
                  scrollToRef={ratingsReviewHeader}
                />
              </div>
            )}
            <ProductPricing
              sponsored={sponsored}
              percentOff={displayedPercentOff}
              salePrice={displayedSalePrice}
              price={displayedPrice}
            />
            {isParisHiltonKit && (
              <div className={styles.label}>MEMBER-EXCLUSIVE KIT</div>
            )}
            {loggedIn && isSubscribableShopProduct && (
              <>
                <SubscriptionSelector
                  isSubscriptionAvailable={isSubscriptionAvailable}
                  isOneTimePurchaseAvailable={!selectedVariant.soldOut}
                  hasActiveSubscription={selectedVariant.hasActiveSubscription}
                  productPurchaseOption={productPurchaseOption}
                  subscriptionPrice={subscriptionPrice}
                  subscriptionPercentOff={subscriptionPercentOff}
                  oneTimePrice={salePrice}
                  subscribableProducts={subscribableProducts}
                  handleSubscribeChange={handleSubscribeChange}
                />
                <SubscriptionModalConfirmation
                  isOpen={showSubscriptionModal}
                  productPurchaseOption={productPurchaseOption}
                  isOneTimePurchaseAvailable={!selectedVariant.soldOut}
                  isSubscriptionAvailable={isSubscriptionAvailable}
                  onClose={cancelSubscriptionModal}
                  onConfirm={confirmSubscriptionModal}
                />
              </>
            )}
            {!sponsored && _.size(variants) > 1 && (
              <Variants
                variants={variants}
                selectedVariant={selectedVariant}
                selectedDescriptors={selectedDescriptors}
                updateSelectedVariant={updateSelectedVariant}
                updateSelectedDescriptors={updateSelectedDescriptors}
                context="accordion"
              />
            )}
            {isSubscribeSaveSelected && <SubscribeAndSaveDetails />}
            {isMobile && shortDescription && (
              <div className={styles.overview}>
                <h3>Product Overview</h3>
                <p>{shortDescription}</p>
              </div>
            )}
            <div
              className={classNames(styles.buttonsContainer, {
                [styles.webview]: isWebview,
              })}
            >
              <div className={styles.buttonContainer}>
                {isParisHiltonKit && selectedVariant.soldOut && !loggedIn ? (
                  <NotifyMeButton />
                ) : (
                  <UpdateCartButton
                    productName={name}
                    sponsored={sponsored}
                    nonColorVariant={false}
                    shareUrl={shareUrl}
                    viewSize={viewSize}
                    subtotal={subtotal}
                    hasSponsoredProductInCart={hasSponsoredProductInCart}
                    sponsoredThreshold={threashold}
                    isGwp={gwp}
                    variant={selectedVariant}
                    buttonLocation={
                      referrerProductId
                        ? btnLocationEnum.recommendedAccordion
                        : btnLocationEnum.productAccordion
                    }
                    referrerProductId={referrerProductId}
                    positionOrder={positionOrder}
                    gwpProducts={gwpProducts}
                    productPurchaseOption={productPurchaseOption}
                    hasActiveSubscription={
                      selectedVariant.hasActiveSubscription
                    }
                  />
                )}
              </div>
              {isSocialEcomm && selectedVariant && (
                <div className={styles.shareButton}>
                  <Button
                    onClick={() => this.handleClickShare(percentOff, imageUrl)}
                    variation="black"
                    containerFullWidth
                  >
                    Share & Send Invite
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.productDetails}>
            <CollapsibleSection
              title={<h2>Product Details</h2>}
              isOpen={isDetailsOpen}
              toggleIsOpen={() => {
                this.toggleProductCollapsible('isDetailsOpen');
              }}
            >
              <ProductDescription
                layout="small"
                shortDescription={!isMobile && shortDescription}
                isParisHiltonKit={isParisHiltonKit}
                includes={includes}
                features={features}
                color={color}
                design={design}
                size={size}
                dimensions={dimensions}
                howToUse={howToUse}
                ingredients={ingredients}
                materials={materials}
                techCompatibility={techCompatibility}
                careInstructions={careInstructions}
                pao={pao}
                additionalInfo={additionalInformation}
                countryOfOrigin={countryOfOrigin}
                dropshipDisclaimer={dropshipDisclaimer}
                paoDisclaimer={paoDisclaimer}
                cbdDisclaimer={cbdDisclaimer}
                description={description}
                shortDescriptionFieldsFF={shortDescriptionFieldsFF}
                loggedIn={loggedIn}
              />
            </CollapsibleSection>
            {!loggedIn && (
              <FeatureList
                title="A Nice Deal for Members and Brands"
                subtitle="Guaranteeing that our members get the best deals out there."
                list={PH_FEATURE_LIST_BEIGE}
                variation="icons"
              />
            )}
            <div className={ratingStyles.divider} />
            {rateAndReviewFF && !!productRatingGraph && (
              <CollapsibleSection
                title={
                  <ProductRatingsTitle
                    totalReviewCount={productRatingGraph.totalReviewCount}
                    writtenReviewsCount={productRatingGraph.writtenReviewsCount}
                    averageRating={productRatingGraph.averageRating}
                    isOpen={isRatingsOpen}
                  />
                }
                isOpen={isRatingsOpen}
                toggleIsOpen={() => {
                  this.toggleProductCollapsible('isRatingsOpen');
                }}
                ref={this.reviewsHeaderRef}
              >
                <ProductRatings
                  hasReviews={hasReviews}
                  productRatingGraph={productRatingGraph}
                  productId={id}
                  productBrand={brandName}
                  productName={name}
                  loggedIn={loggedIn}
                  productHasReviewed={hasBeenReviewed}
                  sku={selectedVariant.sku}
                  imgUrl={imageUrl}
                  updateReviewedProducts={this.props.receiveProductHasReviewed}
                  scrollToRef={ratingsReviewHeader}
                />
              </CollapsibleSection>
            )}
          </div>
          <SimilarProducts
            products={similarProducts}
            referrerProductId={legacyPostId}
            hasSponsoredProductInCart={hasSponsoredProductInCart}
            sponsoredThreshold={threashold}
            subtotal={subtotal}
            gwpProducts={gwpProducts}
            viewSize={viewSize}
          />
        </div>
      </section>
    );
  }
}

function mapStateToProps(
  state: State,
  props: {| ...OwnProps, +selectedCategoryId: string | null |}
): StatePropsForWithSelectedVariant {
  // these are the product props being viewed in the accordion
  const { id, defaultVariantId, selectedCategoryId, variants } = props;

  return {
    sponsoredThreshold: state.campaignState.sponsoredThreshold,
    subtotal: selectSubtotal(state),
    variantToRemove: state.productsState.variantToRemove,
    viewSize: state.viewState.viewSize,
    trackingData: selectTrackingData(state, {
      productId: id,
      variantId: defaultVariantId || _.get(variants, [0, 'id']),
      selectedCategoryId,
    }),
    hasSponsoredProductInCart: selectHasSponsoredProductInCart(state),
    overrideDefaultVariantId: state.productsState.overrideDefaultVariantId,
    teamId: state.socialEcommState.team.teamId,
    isSocialEcomm: selectIsSocialEcomm(state, props),
    similarProducts: selectProductRecommendations(state),
    fetchingSimilarProducts: state.productsState.fetchingProductRecommendations,
    ratings: state.productsState.ratings,
    productRatingGraph: state.productsState.productRatingGraph,
    reviewedProducts: state.productsState.reviewedProducts,
    subscribableProducts: state.productsState.subscribableProducts,
    loggedIn: state.userState.loggedIn,
  };
}

export default (compose(
  withSelectedCategoryId,
  withSelectedVariant(false),
  withLDConsumer(),
  withVersion,
  connect<
    {|
      ...OwnProps,
      ...StatePropsForWithSelectedVariant,
      ...DispatchProps,
      +selectedCategoryId: string | null,
      +version: string | null,
    |},
    {|
      ...OwnProps,
      +selectedCategoryId: string | null,
      +version: string,
    |},
    _,
    _,
    _,
    _
  >(mapStateToProps, {
    hideSponsoredWarning,
    requestAddToCart,
    requestRemoveFromCart,
    setOverrideDefaultVariantId,
    openShareInviteModal: openShareInviteModalAction,
    requestProductRecommendations: requestProductRecommendationsAction,
    requestRatingGraph,
    requestProductHasReviewed,
    receiveProductHasReviewed,
    requestClearVariantItemsFromCart,
  })
)(ProductAccordion): React.AbstractComponent<OwnProps>);
